import React, { ReactElement, Fragment } from 'react';
import { SortOption } from '../../../lib/searchspring/client';
import { useRouter } from 'next/router';
import { Listbox, Transition } from '@headlessui/react';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import SortIcon from '@material-ui/icons/Sort';
import { newPathWithQueryParam, removeMatchingQueryParam } from 'lib/product';

export type LinkedSortOption = SortOption & {
  href: string;
};

export default function Sort({
  sortOptions,
  currentSort,
  sticky,
  isMobile,
}: {
  sortOptions: SortOption[];
  currentSort: SortOption;
  sticky: boolean;
  isMobile: boolean;
}): ReactElement {
  const router = useRouter();

  const linkedSortOptions = sortOptions.map((option) => {
    const hrefWithoutSortAndWithoutPage = removeMatchingQueryParam(
      removeMatchingQueryParam(router.asPath, 'sort.'),
      'page'
    );
    const href =
      option.field === ''
        ? hrefWithoutSortAndWithoutPage
        : newPathWithQueryParam(
            hrefWithoutSortAndWithoutPage,
            `sort.${option.field}`,
            option.direction
          );

    return {
      ...option,
      href,
    };
  });

  const onChange = (option: LinkedSortOption): void => {
    router.push(option.href);
  };

  const MiniSort = (): ReactElement => (
    <Listbox.Button className="focus:outline-none ml-5  font-semibold">
      Sort <SortIcon />
    </Listbox.Button>
  );

  const LargeSort = (): ReactElement => (
    <Listbox.Button className="w-full flex items-center justify-between px-4 py-1 rounded focus:outline-none border border-gray-400 bg-white">
      <div className="">
        <div className="text-xs text-left text-gray-600">Sort By:</div>
        <div className=" font-semibold">{currentSort.label}</div>
      </div>
      <KeyboardArrowDownIcon />
    </Listbox.Button>
  );

  return (
    <Listbox
      as="div"
      value={currentSort}
      onChange={(value) => onChange((value as unknown) as LinkedSortOption)}
      className={`relative inline-block text-left mr-1 w-1/2 ${sticky ? 'md:w-auto' : 'md:w-56'}`}
    >
      {isMobile || !sticky ? <LargeSort /> : <MiniSort />}

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Listbox.Options
          className="absolute z-20 left-0 md:left-auto md:right-0 w-56 mt-2 px-3 py-2 bg-white rounded-md shadow focus:outline-none overflow-scroll"
          style={{ width: '210px' }}
        >
          {linkedSortOptions.map((option) => (
            <Listbox.Option key={option.field + option.direction} value={option}>
              {({ active }) => (
                <button
                  className={`flex items-center p-1 w-full rounded ${
                    currentSort.label === option.label && !active
                      ? 'bg-gray-300'
                      : active
                      ? 'text-white bg-tertiary'
                      : 'text-gray-700'
                  }`}
                >
                  {option.label}
                </button>
              )}
            </Listbox.Option>
          ))}
        </Listbox.Options>
      </Transition>
    </Listbox>
  );
}
